import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, PawPrint, Stethoscope, UserCog, Calendar, FileText, Tag, CreditCard, LogOut, Gift, Clipboard, Package, ChevronDown, Settings, Syringe, Menu, X, Building2, Pill, MessageSquare, Bell, CheckCheck } from 'lucide-react';
import logo from '@/assets/logo.png';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getUser, logout, api } from '@/utils/api';
import { toast } from 'sonner';

const NotificationBell = ({ notifications, unreadCount, onMarkAllRead, onNotificationClick, onViewAll }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative inline-flex items-center justify-center p-2 rounded-lg text-gray-500 hover:bg-sage-50 hover:text-sage-700 transition-colors"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-80 p-0 shadow-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <span className="font-semibold text-sm text-gray-800">Notifications</span>
          {unreadCount > 0 && (
            <button
              onClick={onMarkAllRead}
              className="flex items-center gap-1 text-xs text-sage-600 hover:text-sage-800 transition-colors"
            >
              <CheckCheck className="w-3.5 h-3.5" />
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-gray-400 gap-2">
              <Bell className="w-8 h-8" />
              <p className="text-sm">No notifications yet</p>
            </div>
          ) : (
            notifications.slice(0, 10).map((notification) => (
              <button
                key={notification.id}
                onClick={() => { setOpen(false); onNotificationClick(notification); }}
                className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-sage-50 transition-colors flex gap-3 items-start ${
                  !notification.is_read ? 'bg-sage-50/60' : ''
                }`}
              >
                {!notification.is_read && (
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-sage-500 shrink-0" />
                )}
                <div className={`flex-1 min-w-0 ${notification.is_read ? 'pl-5' : ''}`}>
                  <p className={`text-sm font-medium truncate ${
                    notification.is_read ? 'text-gray-600' : 'text-sage-800'
                  }`}>
                    {notification.title}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5 line-clamp-2 whitespace-normal">
                    {notification.content}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>
        {notifications.length > 0 && (
          <div className="px-4 py-2 border-t border-gray-100">
            <button
              onClick={() => { setOpen(false); onViewAll(); }}
              className="w-full text-center text-xs text-sage-600 hover:text-sage-800 py-1 transition-colors"
            >
              View all notifications
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [expandedMenu, setExpandedMenu] = useState(() => {
    // Auto-expand the submenu whose child is currently active
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      if (
        path.startsWith('/dashboard/diagnoses') ||
        path.startsWith('/dashboard/vaccinations') ||
        path.startsWith('/dashboard/treatments') ||
        path.startsWith('/dashboard/prescriptions') ||
        path.startsWith('/dashboard/visit-diagnoses') ||
        path.startsWith('/dashboard/visit-service-records')
      ) {
        return 'Service Records';
      }
      if (
        path.startsWith('/dashboard/pet-types') ||
        path.startsWith('/dashboard/visit-types') ||
        path.startsWith('/dashboard/services') ||
        path.startsWith('/dashboard/clinic')
      ) {
        return 'Settings';
      }
    }
    return null;
  });

  useEffect(() => {
    api.get('/notifications')
      .then((res) => setNotifications(res.data))
      .catch(() => {});
  }, [location.pathname]);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const handleMarkAllRead = async () => {
    try {
      await api.patch('/notifications/mark-all-read');
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch {
      toast.error('Failed to mark all as read');
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      try {
        await api.put(`/notifications/${notification.id}`, { is_read: true });
        setNotifications((prev) =>
          prev.map((n) => n.id === notification.id ? { ...n, is_read: true } : n)
        );
      } catch {
        // silent
      }
    }
    navigate('/dashboard/notifications');
  };

  const handleViewAll = () => navigate('/dashboard/notifications');

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/dashboard/owners', icon: Users, label: 'Owners' },
    { path: '/dashboard/pets', icon: PawPrint, label: 'Pets' },
    { path: '/dashboard/doctors', icon: UserCog, label: 'Doctors' },
    { path: '/dashboard/visits', icon: Clipboard, label: 'Visits' },
    {
      icon: Stethoscope,
      label: 'Service Records',
      submenu: [
        { path: '/dashboard/vaccinations', icon: Syringe, label: 'Vaccination Records' },
        { path: '/dashboard/treatments', icon: Stethoscope, label: 'Treatment Records' },
        { path: '/dashboard/visit-diagnoses', icon: FileText, label: 'Diagnosis Records' },
        { path: '/dashboard/prescriptions', icon: Pill, label: 'Prescription Records' },
        { path: '/dashboard/visit-service-records', icon: Package, label: 'Services Records' },
      ]
    },
    { path: '/dashboard/schedules', icon: Calendar, label: 'Schedules' },
    {
      icon: Settings,
      label: 'Settings',
      submenu: [
        { path: '/dashboard/clinic', icon: Building2, label: 'Clinic Profile' },
        { path: '/dashboard/pet-types', icon: Tag, label: 'Pet Types' },
        { path: '/dashboard/visit-types', icon: FileText, label: 'Visit Types' },
        { path: '/dashboard/services', icon: Package, label: 'Services' },
      ]
    },
    { path: '/dashboard/subscription', icon: CreditCard, label: 'Subscription' },
    { path: '/dashboard/referral', icon: Gift, label: 'Referrals' },
    { path: '/dashboard/notifications', icon: Bell, label: 'Notifications' },
    { path: '/dashboard/contact', icon: MessageSquare, label: 'Contact Us' },
  ];

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div data-theme-scope="dashboard" className="min-h-screen bg-cream flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 md:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Mobile top bar */}
      <div className="fixed top-0 left-0 right-0 h-14 bg-white border-b border-sage-100 flex items-center px-4 gap-3 z-20 md:hidden">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSidebarOpen(true)}
          className="p-2"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </Button>
        <Link to="/dashboard" className="flex items-center gap-2">
          <img src={logo} alt="VetCare" className="h-6 w-auto" />
          <span className="text-lg font-heading font-bold text-sage-700">VetCare</span>
        </Link>
        <div className="ml-auto">
          <NotificationBell
            notifications={notifications}
            unreadCount={unreadCount}
            onMarkAllRead={handleMarkAllRead}
            onNotificationClick={handleNotificationClick}
            onViewAll={handleViewAll}
          />
        </div>
      </div>

      <aside className={`fixed md:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-sage-100 flex flex-col transform transition-transform duration-200 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
        <div className="p-6 border-b border-sage-100">
          <div className="flex items-center justify-between mb-4 md:mb-0">
            <Link to="/dashboard" className="flex items-center gap-2" onClick={closeSidebar}>
              <img src={logo} alt="VetCare" className="h-8 w-auto" />
              <span className="text-2xl font-heading font-bold text-sage-700">VetCare</span>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={closeSidebar}
              className="p-1 md:hidden"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          <div className="py-4 border-b border-slate-100">
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-building2 h-4 w-4 text-emerald-600"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path><path d="M10 6h4"></path><path d="M10 10h4"></path><path d="M10 14h4"></path><path d="M10 18h4"></path></svg></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{user?.clinic_name}</p>
                  <div className="inline-flex items-center rounded-md border px-1 py-0.5 font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80 mt-1 text-xs capitalize">{user?.subscription_plan} Plan</div>
                </div>
                </div>
            </div>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {navItems.map((item) => {
              const isActive = item.path && location.pathname === item.path;
              const isSubmenuExpanded = expandedMenu === item.label;

              if (item.submenu) {
                return (
                  <li key={item.label}>
                    <button
                      onClick={() => setExpandedMenu(isSubmenuExpanded ? null : item.label)}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-gray-700 hover:bg-sage-50"
                      data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium flex-1 text-left">{item.label}</span>
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${
                          isSubmenuExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {isSubmenuExpanded && (
                      <ul className="mt-2 ml-4 space-y-2 border-l-2 border-sage-200 pl-2">
                        {item.submenu.map((subitem) => {
                          const isSubActive = location.pathname === subitem.path;
                          return (
                            <li key={subitem.path}>
                              <Link
                                to={subitem.path}
                                onClick={closeSidebar}
                                className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all ${
                                  isSubActive
                                    ? 'bg-sage-500 text-white shadow-lg'
                                    : 'text-gray-700 hover:bg-sage-50'
                                }`}
                                data-testid={`nav-${subitem.label.toLowerCase().replace(' ', '-')}`}
                              >
                                <subitem.icon className="w-5 h-5" />
                                <span className="font-medium">{subitem.label}</span>
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              }

              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={closeSidebar}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive
                        ? 'bg-sage-500 text-white shadow-lg'
                        : 'text-gray-700 hover:bg-sage-50'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="p-4 border-t border-sage-100">
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-red-600 hover:bg-red-50 rounded-xl"
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Logout
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto pt-14 md:pt-0">
        {/* Desktop top header */}
        <div className="hidden md:flex items-center justify-end px-6 py-3 bg-white border-b border-sage-100 sticky top-0 z-10">
          <NotificationBell
            notifications={notifications}
            unreadCount={unreadCount}
            onMarkAllRead={handleMarkAllRead}
            onNotificationClick={handleNotificationClick}
            onViewAll={handleViewAll}
          />
        </div>
        <Outlet />
      </main>
    </div>
  );
};
